import { Type, plainToInstance } from 'class-transformer'
import { Entity } from '@core/entities/entity'
import { IProjectPlanPermits } from './IProjectPlanPermits'
import { Permit } from '@core/entities/permit/Permit'
import { ProjectPlanDashboard } from '../ProjectPlanDashboard'
import { Pin } from '@core/entities/skia/Pin'
import uniqBy from 'lodash/uniqBy'

export class ProjectPlanPermits extends Entity<IProjectPlanPermits> {
  @Type(() => ProjectPlanDashboard)
  permitStats: ProjectPlanDashboard

  @Type(() => Permit)
  permits: Permit[]

  public static new(payload: unknown): ProjectPlanPermits {
    const entity = plainToInstance(ProjectPlanPermits, payload)

    return entity
  }

  public getSelectedPermits(selectedAreas: string[]): Permit[] {
    if (selectedAreas?.length === 0) return this.permits
    return this.permits.filter((permit) =>
      permit.projectLocations.areas.some((area) =>
        area.selectedAreas.some((selectedArea) =>
          selectedAreas?.includes(selectedArea.uuid),
        ),
      ),
    )
  }

  public getPermit(permitId: number): Permit {
    return this.permits.find((permit) => permit.id === permitId)
  }

  public getPins(initialSelectedPins: Pin[], excludePermitId?: number): Pin[] {
    initialSelectedPins.forEach((pin) => {
      pin.isNew = true
    })

    const permits = this.permits.filter(
      (permit) => permit.id !== excludePermitId,
    )

    const pins = permits
      .flatMap((permit) => {
        const droppedPins = permit.projectLocations.pins?.[0]?.droppedPins
        const droppedPinsIds = droppedPins?.map((p) => p.uuid)
        return (
          droppedPins?.map((pin) => {
            pin.pinsInGroupIds = droppedPinsIds
            pin.permitId = permit.id
            return pin
          }) ?? []
        )
      })
      .concat(initialSelectedPins)

    const uniquePins = uniqBy(pins, 'uuid').map(Pin.new)

    return uniquePins
  }

  public isShapeSelected(shapeId: string): boolean {
    const shapeSelected = !!this.permits.find((permit) =>
      permit.projectLocations.areas.find((area) =>
        area.selectedAreas.find(
          (selectedArea) => selectedArea.uuid === shapeId,
        ),
      ),
    )

    return shapeSelected
  }

  public getPermitByPin(pin?: Pin): Permit | null {
    return this.permits.find((permit) =>
      permit.projectLocations.pins?.[0]?.droppedPins?.find(
        (p) => p.uuid === pin?.uuid,
      ),
    )
  }

  public getFilteredPermits(
    selectedShapeIds: string[],
    currentPermitId?: number,
    selectedByPinPermitId?: number,
  ): Permit[] {
    const currentPermit = this.permits?.find(
      (permit) => permit.id === currentPermitId,
    )
    const selectedByPinPermit = this.permits?.find(
      (permit) => permit.id === selectedByPinPermitId,
    )

    const filteredProjectPlanPermits =
      this.getSelectedPermits(selectedShapeIds) || this.permits || []

    // If there's no current permit, just return all permits
    if (!currentPermit && !selectedByPinPermit) {
      return filteredProjectPlanPermits
    }

    const isSelectedByPinPermitInOtherPermits = filteredProjectPlanPermits.some(
      (permit) => permit.id === selectedByPinPermitId,
    )

    const isCurrentPermitInOtherPermits = filteredProjectPlanPermits.some(
      (permit) => permit.id === currentPermitId,
    )

    if (!isSelectedByPinPermitInOtherPermits) {
      const priorityPermits = [
        isCurrentPermitInOtherPermits && currentPermit,
        selectedByPinPermit,
      ].filter(Boolean)

      return uniqBy([...priorityPermits, ...filteredProjectPlanPermits], 'id')
    }

    const priorityPermits = [
      isCurrentPermitInOtherPermits && currentPermit,
    ].filter(Boolean)
    return uniqBy([...priorityPermits, ...filteredProjectPlanPermits], 'id')
  }
}
